import { Dialog, DialogContent } from "@/components/ui/dialog";
import { CheckRadioIcon } from "@/components/Icons/CheckRadioIcon";
import { PowerIcon } from "@/components/Icons/PowerIcon";
import { GpuUnavailabilityPolicy } from "@/types";
import { cn } from "@/utils/styles";
import { useEffect, useState } from "react";

type GpuUnavailabilityInfoModalProps = {
  isOpen: boolean;
  initialTab: GpuUnavailabilityPolicy;
  onClose: () => void;
};

type ScenarioContainer = {
  running: boolean;
  /** Runtime the container ends up with, in hours (only meaningful when running). */
  hours: number;
};

type Scenario = {
  key: GpuUnavailabilityPolicy;
  tabLabel: string;
  headline: string;
  containers: ScenarioContainer[];
  outcome: string;
  accent: "blue" | "green" | "orange";
};

const BOOKED_CONTAINERS = 2;
const BOOKED_HOURS = 2;

const SCENARIOS: Scenario[] = [
  {
    key: "partial_refund",
    tabLabel: "Refund the rest",
    headline: "1 of 2 containers couldn't start.",
    containers: [
      { running: true, hours: 2 },
      { running: false, hours: 0 }
    ],
    outcome:
      "The running container keeps its 2 hours, and you're refunded for the one that couldn't start.",
    accent: "blue"
  },
  {
    key: "extend_booked",
    tabLabel: "Extend the rest",
    headline: "1 of 2 containers couldn't start.",
    containers: [
      { running: true, hours: 4 },
      { running: false, hours: 0 }
    ],
    outcome:
      "No refund. The 2 hours from the container that couldn't start roll over, so your running container stays up for 4 hours.",
    accent: "green"
  },
  {
    key: "full_refund",
    tabLabel: "Fail cluster",
    headline: "1 of 2 containers couldn't start.",
    containers: [
      { running: false, hours: 0 },
      { running: false, hours: 0 }
    ],
    outcome: "Because the cluster can't run in full, we cancel everything and refund you in full.",
    accent: "orange"
  }
];

const ACCENT_STYLES: Record<
  Scenario["accent"],
  { tile: string; iconWrap: string; text: string; outcomeBg: string }
> = {
  blue: {
    tile: "border-[#3291ff] bg-gradient-to-br from-[rgba(50,145,255,0.10)] to-transparent",
    iconWrap: "bg-[rgba(50,145,255,0.12)] text-[#3291ff]",
    text: "text-[#3291ff]",
    outcomeBg: "bg-[rgba(50,145,255,0.08)]"
  },
  green: {
    tile: "border-[#40ba6f] bg-gradient-to-br from-[rgba(64,186,111,0.10)] to-transparent",
    iconWrap: "bg-[rgba(64,186,111,0.14)] text-[#40ba6f]",
    text: "text-[#40ba6f]",
    outcomeBg: "bg-[rgba(64,186,111,0.08)]"
  },
  orange: {
    tile: "border-[#e6770a] bg-gradient-to-br from-[rgba(230,119,10,0.10)] to-transparent",
    iconWrap: "bg-[rgba(230,119,10,0.12)] text-[#e6770a]",
    text: "text-[#e6770a]",
    outcomeBg: "bg-[rgba(230,119,10,0.08)]"
  }
};

const ContainerTile: React.FC<{
  container: ScenarioContainer;
  index: number;
  accent: Scenario["accent"];
}> = ({ container, index, accent }) => {
  const styles = ACCENT_STYLES[accent];
  return (
    <div
      className={cn(
        "flex flex-1 flex-col gap-2 rounded-[10px] border p-4 transition-colors",
        container.running ? styles.tile : "border-dashed border-border bg-muted/40"
      )}
    >
      <div className="flex items-center justify-between">
        <span className="text-[13px] font-medium leading-5 text-foreground">
          Container {index + 1}
        </span>
        <span
          className={cn(
            "flex h-6 w-6 items-center justify-center rounded-[6px]",
            container.running ? styles.iconWrap : "bg-muted text-muted-foreground"
          )}
        >
          {container.running ? (
            <CheckRadioIcon className="h-4 w-4" />
          ) : (
            <PowerIcon className="h-3.5 w-3.5" />
          )}
        </span>
      </div>
      <span
        className={cn(
          "text-[12px] font-medium leading-4",
          container.running ? styles.text : "text-muted-foreground"
        )}
      >
        {container.running ? `Running ${container.hours}h` : "Couldn't start"}
      </span>
    </div>
  );
};

export const GpuUnavailabilityInfoModal: React.FC<GpuUnavailabilityInfoModalProps> = ({
  isOpen,
  initialTab,
  onClose
}) => {
  const [activeKey, setActiveKey] = useState<GpuUnavailabilityPolicy>(initialTab);

  // Sync the visible tab to the option the user opened the modal from.
  useEffect(() => {
    if (isOpen) setActiveKey(initialTab);
  }, [isOpen, initialTab]);

  const scenario = SCENARIOS.find((s) => s.key === activeKey) ?? SCENARIOS[0];
  const styles = ACCENT_STYLES[scenario.accent];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[540px]" onClose={onClose}>
        <div className="flex flex-col gap-1">
          <h2 className="title-h5 text-foreground">What happens when GPUs run short</h2>
          <p className="subtext-small text-muted-foreground">
            Say you book {BOOKED_CONTAINERS} containers for {BOOKED_HOURS} hours each, and only one
            can start. Here's how each option handles it.
          </p>
        </div>

        <div className="flex items-center gap-1 rounded-[10px] border border-border bg-muted/40 p-1">
          {SCENARIOS.map((s) => {
            const isActive = s.key === activeKey;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => setActiveKey(s.key)}
                className={cn(
                  "flex-1 rounded-[7px] px-3 py-1.5 text-[13px] font-medium leading-5 transition-colors",
                  isActive
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {s.tabLabel}
              </button>
            );
          })}
        </div>

        <div className="flex flex-col gap-4 rounded-[12px] border border-border bg-gradient-to-b from-muted/30 to-transparent p-4">
          <p className="text-[14px] font-medium leading-5 text-foreground">{scenario.headline}</p>

          <div className="flex gap-3">
            {scenario.containers.map((container, index) => (
              <ContainerTile
                key={index}
                index={index}
                container={container}
                accent={scenario.accent}
              />
            ))}
          </div>

          <div className={cn("rounded-[8px] px-3 py-2.5", styles.outcomeBg)}>
            <p className="text-[13px] leading-5 text-foreground">{scenario.outcome}</p>
          </div>
        </div>

        <p className="text-[12px] leading-4 text-muted-foreground">
          You only ever pay for compute that actually runs. The right choice depends on whether you
          prefer money back or more runtime from what you've already paid.
        </p>
      </DialogContent>
    </Dialog>
  );
};
